import { useCallback, useMemo, useReducer, useRef } from "react";
import { uploadBookingDoc } from "./bookingDocs";
import { hasFailed, isUploading, itemsOf, uploadsReducer, urlsOf } from "./bookingUploads";

let nextId = 1;

// Uploads each file as soon as it is picked and tracks its progress.
//   slots:    { estimate: { stage: "estimate", multiple: false }, ... }
//   getNames: () => ({ customerName, vehicle }), read at upload time (they only
//             decide the stored file's name, so they come from the form so far)
export function useBookingUploads({ slots, getNames }) {
  const [state, dispatch] = useReducer(uploadsReducer, {});
  const files = useRef(new Map());

  const upload = useCallback(
    async (slot, id, index, total) => {
      try {
        const url = await uploadBookingDoc(files.current.get(id), {
          stage: slots[slot].stage,
          ...getNames(),
          index,
          total,
        });
        dispatch({ type: "done", slot, id, url });
      } catch (err) {
        dispatch({ type: "fail", slot, id, error: err?.message || "Upload failed" });
      }
    },
    [slots, getNames]
  );

  const pick = useCallback(
    (slot, picked) => {
      const { multiple } = slots[slot];
      const have = multiple ? state[slot]?.length || 0 : 0;
      const list = multiple ? picked : picked.slice(0, 1);
      list.forEach((file, i) => {
        const id = `u${nextId++}`;
        files.current.set(id, file);
        dispatch({ type: "add", slot, multiple, item: { id, name: file.name, size: file.size, status: "uploading", url: "", error: "" } });
        upload(slot, id, have + i, have + list.length);
      });
    },
    [slots, state, upload]
  );

  const remove = useCallback((slot, id) => {
    files.current.delete(id);
    dispatch({ type: "remove", slot, id });
  }, []);

  const retry = useCallback(
    (slot, id) => {
      dispatch({ type: "retry", slot, id });
      upload(slot, id, 0, 1);
    },
    [upload]
  );

  return useMemo(
    () => ({
      items: (slot) => itemsOf(state, slot),
      urls: (slot) => urlsOf(state, slot),
      failed: (slot) => hasFailed(state, slot),
      busy: isUploading(state),
      pick,
      remove,
      retry,
    }),
    [state, pick, remove, retry]
  );
}
